import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../app/store";
import { login, setLanguage } from "../../state/user/userSlice";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LanguageSelector } from "../common/LanguageSelector";
import { LanguageCode } from "../common/Language";
import { useNotifications } from "../common/notification/NotificationContext";

export function Login() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const { addNotification } = useNotifications();

  const user = useSelector((state: RootState) => state.user);
  const dispatch = useDispatch<AppDispatch>();

  const [selectedLanguage, setSelectedLanguage] = useState(user.language);

  useEffect(() => {
    if (user.user?.id != null) {
      navigate(`/user/${user.user.id}/home`);
    }
  }, [user, navigate]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const username = data.get("username")?.toString() || "";
    const password = data.get("password")?.toString() || "";

    try {
      await dispatch(login({ username, password })).unwrap();
    } catch (error) {
      addNotification(
        t("login.error_credentials", {
          defaultValue: "Invalid credentials. Please try again.",
        }),
        "warning"
      );
    }
  };

  function languageChangeHandler(lang: LanguageCode): void {
    setSelectedLanguage(lang);
    dispatch(setLanguage(lang))
  }

  return (
    <Container component="main" maxWidth="xs" sx={{ position: "relative" }}>
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <LanguageSelector
          languages={[LanguageCode.CS, LanguageCode.SK, LanguageCode.EN]}
          selectedLanguage={selectedLanguage}
          onLanguageChange={languageChangeHandler}
        />

        <Typography component="h1" variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
          {t("login.welcome_message_heading")}
        </Typography>
        <Typography component="p" sx={{ color: "text.secondary", mb: 4 }}>
          {t("login.welcome_message_description")}
        </Typography>

        {/* LOGIN FORM */}
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="username"
            label={t("login.username")}
            name="username"
            autoFocus
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label={t("login.password")}
            type="password"
            id="password"
          />
          <Box sx={{ display: "flex", gap: 2, mt: 3, mb: 2 }}>
            <Button
              variant="contained"
              sx={{ flex: 1, whiteSpace: "nowrap" }}
              onClick={() => navigate("/onboarding")}
            >
              {t("login.start_onboarding")}
            </Button>
            <Button type="submit" variant="outlined" sx={{ flex: 1 }}>
              {t("login.login")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}
